// The zoning map + "Adopted Zoning Regulations" essay of the Property page (originally the Cognito form's
// Property → Zoning Map & Adopted Zoning Regulations). The essay is the tenant's — legal content of the
// place its firm works in — so its text and map live in a workspace setting (server: services/ddPoaContent.ts,
// client: utils/ddPoaContent.ts) and this file keeps only the generic reading of it: which paragraph covers
// which zone, and where the essay's three blocks begin. A paragraph flagged `always` is never greyed,
// whatever zone is selected (owner's explicit call, 2026-08-12 — kept with the essay text in the setting).
import type { ZoningEntry } from '../../utils/api/ddpoa';

export type { ZoningEntry };

// ── Which zone(s) does each essay paragraph describe? ────────────────────────
// Used to highlight only the selected zone's rules and mute the rest. Coverage comes from the
// paragraph's leading bold header ("**Zones 6.1 to 6.11**", "**Zone 4**", "**In Zones 6.3 and 6.9**");
// paragraphs without a header (rule lists, "In these zones…") inherit the block above them.
// A paragraph that says "other than zones 6.3 and 6.9" EXCLUDES those, so 6.3 never highlights the
// rules written for the other green zones.
const ZONE_RANGE = /(\d+)\.(\d+)\s*(?:to|–|-)\s*(\d+)\.(\d+)/g;
const ZONE_ONE = /\b(\d+)\.(\d+)\b/g;
const ZONE_BARE = /\bZone\s+(\d+)\b(?!\.)/g;

function zonesIn(text: string): string[] {
  const out = new Set<string>();
  let m: RegExpExecArray | null;
  const r1 = new RegExp(ZONE_RANGE); 
  const consumed: string[] = [];
  while ((m = r1.exec(text))) {
    const maj = Number(m[1]);
    if (Number(m[3]) === maj) for (let i = Number(m[2]); i <= Number(m[4]); i++) out.add(`${maj}.${i}`);
    consumed.push(m[0]);
  }
  let rest = text; for (const c of consumed) rest = rest.replace(c, ' ');
  const r2 = new RegExp(ZONE_ONE); while ((m = r2.exec(rest))) out.add(`${m[1]}.${m[2]}`);
  const r3 = new RegExp(ZONE_BARE); while ((m = r3.exec(rest))) out.add(m[1]);
  return Array.from(out);
}

export type ZoneCoverage = Array<{ covers: string[]; excludes: string[] }>;
const coverageMemo = new WeakMap<ZoningEntry[], ZoneCoverage>();
/** For each essay entry: the zones it covers and the zones it explicitly excludes (memoised per essay). */
export function zoneCoverage(essay: ZoningEntry[]): ZoneCoverage {
  const hit = coverageMemo.get(essay);
  if (hit) return hit;
  let current: string[] = [];
  const out = essay.map(e => {
    const head = /^\*\*(.+?)\*\*/.exec(e.t);
    if (head && /zone/i.test(head[1])) current = zonesIn(head[1]);
    const ex = /other than zones?\s+([0-9][0-9.,\s]*(?:and[\s0-9.,]*)*)/i.exec(e.t);
    return { covers: current.slice(), excludes: ex ? zonesIn(ex[1]) : [] };
  });
  coverageMemo.set(essay, out);
  return out;
}

/** "Green Zone 6.3" → "6.3"; "Zone 4" → "4"; returns '' when no number is present. */
export function zoneKey(selected: string): string {
  const m = /(\d+\.\d+|\b\d+\b)/.exec(String(selected || ''));
  return m ? m[1] : '';
}

/** Where the essay's blocks begin (-1 when the essay has no such part):
 *  - adopted: the "Adopted Zoning Regulations" heading — the preamble card starts here;
 *  - zones: the first zone-specific paragraph ("**Zones 1.1 to 1.20** …"). Everything from here to the
 *    Zoning Interpretation heading is the per-zone block: the zone that applies is emphasised there and
 *    the rest is greyed. The preamble above it (objectives, policy, "The zones are as follows:") is a
 *    separate card and is never restyled (Calem 2026-08-09);
 *  - interpretation: the "Zoning Interpretation" heading — everything from there down stays as-is. */
export function essayIndexes(essay: ZoningEntry[]): { adopted: number; zones: number; interpretation: number } {
  const interpretation = essay.findIndex(e => e.h && /Zoning Interpretation/i.test(e.t));
  const adopted = essay.findIndex(e => e.h && /Adopted Zoning Regulations/i.test(e.t));
  const zones = essay.findIndex((e, i) => i > adopted && !e.h && /^\*\*Zones?\b[^*]*\d/.test(e.t.trim()));
  return { adopted, zones, interpretation };
}
/** The paragraph the map is shown under (the first naming the "Zoning Map"); -1 when none does. */
export const mapEntryIndex = (essay: ZoningEntry[]): number => essay.findIndex(e => e.t.includes('Zoning Map'));
/** The map's alt text: that paragraph's words without the bold markers ('Zoning Map' when there is none). */
export function mapAltText(essay: ZoningEntry[]): string {
  const i = mapEntryIndex(essay);
  return i >= 0 ? essay[i].t.replace(/\*\*/g, '').trim() : 'Zoning Map';
}
